import { FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { FaMapMarkedAlt, FaUser } from 'react-icons/fa';
import { GiTwoCoins } from 'react-icons/gi';
import { MdEmail, MdPassword, MdPhoneInTalk } from 'react-icons/md';
import { PiDeviceMobileLight } from 'react-icons/pi';
import { RiCoinsLine, RiEdit2Fill } from 'react-icons/ri';
import {
  TbClockCheck,
  TbClockDollar,
  TbCoinTakaFilled,
  TbCurrencyDollarCanadian,
  TbCurrencyTaka,
  TbReceiptDollar,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sectionAnimVariants } from '../../constants/animationVariants';
import { useAuthContext } from '../../contexts/AuthProvider';
import useAuthActions from '../../hooks/APIHooks/users/useAuthActions';
import useClientActions from '../../hooks/APIHooks/users/useClientActions';
import { Client } from '../../types/users';
import {
  convertNumberToLocaleString,
  showFullName,
  showPicture,
} from '../../utils/commonFunctions';
import { deleteToken } from '../../utils/cookieHandler';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import AvailabilityBadge from '../CommonComps/Badges/AvailabilityBadge';
import ClientProfileInfoCard from '../CommonComps/Cards/ClientProfileInfoCard';
import PersonalInfoCard from '../CommonComps/Cards/PersonalInfoCard';
import ChangePasswordModal from '../CommonComps/Modals/ChangePasswordModal';
import UpdateClientProfileModal from '../CommonComps/Modals/UpdateClientProfileModal';

const ClientPersonalInfo = ({ currentClient }: { currentClient: Client }) => {
  // destructuring the current client object here
  const {
    username,
    first_name,
    middle_name,
    last_name,
    email,
    isd_code,
    phone,
    profile_picture,
    address,
    province_name,
    city_name,
    postal_code,
    country_name,
    total_credits_purchased_using_dollars,
    total_credits_purchased_using_manual,
    money_spent,
    dollars_spent,
    minutes_bought,
    minutes_used,
  } = currentClient || {};

  // integration of react hooks here
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showChangeModal, setShowChangeModal] = useState<boolean>(false);

  // integration of context hooks here
  const { setCurrentUserId } = useAuthContext();

  // integration of custom API hooks here
  const { updateClientHandler } = useClientActions('');
  const { userLogoutHandler } = useAuthActions();

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // handler function to handle change client password feature declared here
  const changeClientPasswordHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body = new FormData();

      body.append('current_password', values.current_password);
      body.append('password', values.new_password);
      body.append('updated_by', currentClient.username);

      await updateClientHandler
        .mutateAsync({ clientId: currentClient.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Password Changed!! Please, Login Again..');

            setShowChangeModal(false);

            userLogoutHandler.mutateAsync().then((res) => {
              if (res.is_success) {
                deleteToken();

                setCurrentUserId(0);

                sessionStorage.clear();
                localStorage.clear();

                navigate('/');
              }
            });
          } else {
            toast.error(res.message || 'Password Change Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering client personal info component here
  return (
    <motion.section
      variants={sectionAnimVariants}
      initial='initial'
      animate='animate'
      className='relative p-3 md:p-5 shadow-academia-sm rounded-xl w-full xl:w-4/5 mx-auto'
    >
      <div className='space-y-5'>
        <div className='flex items-center justify-center'>
          {profile_picture ? (
            <div className='relative size-28'>
              <div className='size-full rounded-full overflow-hidden'>
                <img
                  className='size-full object-cover'
                  src={showPicture(profile_picture)}
                  alt={`avatar of ${username}`}
                />
              </div>
              <AvailabilityBadge />
            </div>
          ) : (
            <div
              title='Online'
              className='relative flex items-center justify-center bg-base-light-gray size-28 rounded-full'
            >
              <FaUser className='size-16 text-gray-300' />
              <AvailabilityBadge />
            </div>
          )}
        </div>

        <div className='text-center'>
          <h3 className='font-medium text-2xl text-base-dark-green'>
            {showFullName(first_name, middle_name, last_name)}
          </h3>
          <span className='font-medium text-base text-base-cyan'>
            @{username}
          </span>
        </div>

        <div className='grid md:grid-cols-2 2xl:grid-cols-3 gap-5'>
          <PersonalInfoCard
            icon={<MdEmail className='size-20 text-base-purple' />}
            value={email}
            label='Email'
            color='#845ADF'
            valueFontSize='text-xl'
            isReverse
          />

          <PersonalInfoCard
            icon={<MdPhoneInTalk className='size-20 text-base-orange' />}
            value={`${isd_code}${phone}`}
            label='Phone number'
            color='#FF8042'
            valueFontSize='text-xl'
            isReverse
          />

          <PersonalInfoCard
            icon={<FaMapMarkedAlt className='size-20 text-base-light-blue' />}
            value={`${address}, ${city_name}, ${province_name}, ${postal_code}, ${country_name}`}
            label='From'
            color='#00BCEF'
            valueFontSize='text-xl'
            isReverse
            spanClassName='md:col-span-2 2xl:col-span-1'
          />
        </div>

        <div className='grid md:grid-cols-2 2xl:grid-cols-4 gap-5 pt-5 border-t-2 border-base-cyan/70'>
          <div className='col-span-1 md:col-span-2 2xl:col-span-3 row-span-1 2xl:row-span-2 flex flex-col justify-between'>
            <div className='space-y-3'>
              <ClientProfileInfoCard
                icon={
                  <TbReceiptDollar className='size-16 md:size-8 text-base-light-blue' />
                }
                valueIcon={
                  <TbCurrencyDollarCanadian className='size-8 text-base-light-blue' />
                }
                value={
                  convertNumberToLocaleString(dollars_spent || 0, 2, 2) ||
                  '0.00'
                }
                label='Dollars Payment'
                color='#00BCEF'
              />

              <ClientProfileInfoCard
                icon={
                  <PiDeviceMobileLight className='size-16 md:size-8 text-base-dark-green' />
                }
                valueIcon={
                  <TbCurrencyTaka className='size-8 text-base-dark-green' />
                }
                value={convertNumberToLocaleString(money_spent, 2, 2)}
                label='Manual Payment'
                color='#388229'
              />
            </div>

            <div className='border-t-2 border-dashed border-base-cyan my-3'></div>

            <div className='space-y-3'>
              <ClientProfileInfoCard
                icon={
                  <RiCoinsLine className='size-16 md:size-8 text-base-light-blue' />
                }
                value={total_credits_purchased_using_dollars.toLocaleString()}
                label='Credits Purchased'
                subLabel='(Dollars Payment)'
                color='#00BCEF'
              />

              <ClientProfileInfoCard
                icon={
                  <TbCoinTakaFilled className='size-16 md:size-8 text-base-dark-green' />
                }
                value={total_credits_purchased_using_manual.toLocaleString()}
                label='Credits Purchased'
                subLabel='(Manual Payment)'
                color='#388229'
              />
            </div>

            <div className='border-t-2 border-dashed border-base-cyan my-3'></div>

            <ClientProfileInfoCard
              icon={<GiTwoCoins className='size-16 md:size-8 text-base-cyan' />}
              value={(
                total_credits_purchased_using_dollars +
                total_credits_purchased_using_manual
              ).toLocaleString()}
              label='Total Purchased Credits'
              color='#1ABC9C'
            />
          </div>

          <PersonalInfoCard
            icon={<TbClockDollar className='size-16 text-base-purple' />}
            value={minutes_bought?.toLocaleString() || '0'}
            label='Minutes bought'
            color='#845ADF'
          />

          <PersonalInfoCard
            icon={<TbClockCheck className='size-16 text-base-orange' />}
            value={minutes_used?.toLocaleString() || '0'}
            label='minutes used'
            color='#FF8042'
          />
        </div>
      </div>

      <div className='absolute right-5 top-5 space-y-2'>
        <button
          title='Edit Personal Information'
          onClick={() => setShowEditModal(true)}
          className='flex items-center justify-center bg-gray-200 text-base-dark-gray size-9 rounded-full hover:scale-110 hover:bg-base-light-blue hover:text-base-dark-blue active:scale-95 duration-300'
        >
          <RiEdit2Fill className='size-5' />
        </button>

        <button
          title='Change Password'
          onClick={() => setShowChangeModal(true)}
          className='flex items-center justify-center bg-gray-200 text-base-dark-gray size-9 rounded-full hover:scale-110 hover:bg-base-light-blue hover:text-base-dark-blue active:scale-95 duration-300'
        >
          <MdPassword className='size-5' />
        </button>
      </div>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <UpdateClientProfileModal
            client={currentClient}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showChangeModal && (
          <ChangePasswordModal
            handleModalClose={() => setShowChangeModal(false)}
            changePasswordFn={changeClientPasswordHandler}
            isRequestedFromUser
          />
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default ClientPersonalInfo;
